import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { analyticsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import KeywordSelector from './KeywordSelector';
import SummaryCards from './SummaryCards';
import TrendsChart from './TrendsChart';
import GranularTrendsChart from './GranularTrendsChart';
import SeasonalityChart from './SeasonalityChart';
import SeasonalityTable from './SeasonalityTable';
import MonthlyChangesTable from './MonthlyChangesTable';
import RefreshButton from './RefreshButton';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [seasonality, setSeasonality] = useState(null);
  const [currentKeyword, setCurrentKeyword] = useState('Singapore PR');
  const [currentCountry, setCurrentCountry] = useState('SG');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load all data in parallel with keyword and country
      const [dataResult, summaryResult, chartsResult, seasonalityResult] = await Promise.all([
        analyticsAPI.getData(currentKeyword, currentCountry),
        analyticsAPI.getSummary(currentKeyword, currentCountry),
        analyticsAPI.getCharts(currentKeyword, currentCountry),
        analyticsAPI.getSeasonality(currentKeyword, currentCountry)
      ]);

      setData(dataResult);
      setSummary(summaryResult);
      setCharts(chartsResult);
      setSeasonality(seasonalityResult);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load analytics data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [currentKeyword, currentCountry]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleKeywordChange = (keyword, country) => {
    setCurrentKeyword(keyword);
    setCurrentCountry(country);
  };

  const handleRefreshComplete = () => {
    loadData();
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <LoadingSpinner />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <RefreshButton onRefreshComplete={handleRefreshComplete} />

      {/* Keyword Selection */}
      <KeywordSelector
        onKeywordChange={handleKeywordChange}
        currentKeyword={currentKeyword}
        currentCountry={currentCountry}
      />

      {/* Summary Statistics */}
      <SummaryCards
        summary={summary}
        lastUpdated={data?.last_updated}
      />

      {/* Main Trends Chart */}
      <Row className="mb-4">
        <Col>
          <TrendsChart chartData={charts?.trends_chart} />
        </Col>
      </Row>

      {/* Granular 12-Month Chart */}
      <Row className="mb-4">
        <Col>
          <GranularTrendsChart keyword={currentKeyword} country={currentCountry} />
        </Col>
      </Row>

      {/* Seasonality Analysis */}
      <Row className="mb-4">
        <Col lg={8}>
          <SeasonalityChart chartData={charts?.boxplot_chart} />
        </Col>
        <Col lg={4}>
          <SeasonalityTable seasonalityData={seasonality} />
        </Col>
      </Row>

      {/* Recent Monthly Changes */}
      <Row>
        <Col>
          <MonthlyChangesTable monthlyData={data?.monthly_data} />
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
